// Email/password auth for the web app: sign in, register, and reset password.
// Backed by Firebase via AuthContext (signIn / signUp / sendPasswordReset).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';

// Username: 3–30 chars, letters/numbers/._- (lowercased on save).
const USERNAME_RE = /^[a-zA-Z0-9._-]{3,30}$/;
// Password: at least 8 chars with a letter and a number.
function passwordProblem(pw) {
  if (pw.length < 8) return 'Password must be at least 8 characters.';
  if (!/[a-zA-Z]/.test(pw) || !/[0-9]/.test(pw)) return 'Password must include a letter and a number.';
  return null;
}

const MODES = {
  signin: { title: 'Sign in', cta: 'Sign in', busy: 'Signing in…' },
  signup: { title: 'Create your account', cta: 'Create account', busy: 'Creating…' },
  forgot: { title: 'Reset your password', cta: 'Send reset link', busy: 'Sending…' },
};

export default function LoginPage() {
  const { signIn, signUp, sendPasswordReset, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const switchMode = (m) => { setMode(m); setError(null); setNotice(null); setPassword(''); setConfirm(''); setUsername(''); setFirstname(''); setLastname(''); };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setNotice(null);

    if (mode === 'signup') {
      if (!USERNAME_RE.test(username.trim())) {
        setError('Username must be 3–30 characters (letters, numbers, . _ -).'); return;
      }
      const pwErr = passwordProblem(password);
      if (pwErr) { setError(pwErr); return; }
      if (password !== confirm) { setError('Passwords do not match.'); return; }
    }

    setBusy(true);
    try {
      if (mode === 'signin') {
        await signIn(email.trim(), password);
      } else if (mode === 'signup') {
        // Catch a taken username before creating the login.
        try {
          const chk = await api.usernameAvailable(username.trim());
          if (chk && chk.available === false) {
            setError('That username is taken — please choose another.');
            setBusy(false);
            return;
          }
        } catch { /* can't check — the server still rejects duplicates */ }
        // design/025 — new accounts go straight to the 7-day-trial step.
        // Set the destination before auth flips us into the app.
        navigate('/billing?welcome=1', { replace: true });
        await signUp(email.trim(), password);
        // Account exists + signed in now; set the chosen username on /me.
        // A taken username is non-fatal — they're in, and can change it in Profile.
        try {
          const profile = { username: username.trim() };
          if (firstname.trim()) profile.firstname = firstname.trim();
          if (lastname.trim()) profile.lastname = lastname.trim();
          await api.updateMe(profile);
          await refreshMe();
        } catch (uErr) {
          const taken = (uErr?.message || '').toLowerCase().includes('username');
          alert(taken
            ? 'That username was taken — you’re signed in; pick another in Profile.'
            : 'Signed in, but we couldn’t set your username — set it in Profile.');
        }
        // onAuthStateChanged in AuthContext routes into the app.
      } else {
        await sendPasswordReset(email.trim());
        setNotice('If an account exists for that email, a reset link is on its way.');
      }
    } catch (err) {
      setError(friendly(err));
    } finally {
      setBusy(false);
    }
  };

  const m = MODES[mode];
  // Required-field marker, shown on the sign-up form only.
  const req = mode === 'signup' ? ' *' : '';

  return (
    <div style={styles.wrap}>
      <form style={styles.card} onSubmit={submit}>
        <div style={styles.brand}>
          <img src="/logo.png" alt="Compassion Practice" style={styles.logo} />
          <div style={styles.brandText}>Compassion Practice</div>
        </div>
        <h1 style={styles.title}>{m.title}</h1>

        {mode === 'signup' && (
          <>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={styles.label}>First name</label>
                <input
                  style={styles.input}
                  type="text"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  autoComplete="given-name"
                  placeholder="Jane"
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={styles.label}>Last name</label>
                <input
                  style={styles.input}
                  type="text"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  autoComplete="family-name"
                  placeholder="Doe"
                />
              </div>
            </div>
            <label style={styles.label}>Username{req}</label>
            <input
              style={styles.input}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              placeholder="how others will see you"
              required
            />
          </>
        )}

        <label style={styles.label}>Email{req}</label>
        <input
          style={styles.input}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />

        {mode !== 'forgot' && (
          <>
            <label style={styles.label}>Password{req}</label>
            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
            />
            {mode === 'signup' && (
              <div style={styles.hint}>At least 8 characters, including a letter and a number.</div>
            )}
          </>
        )}

        {mode === 'signup' && (
          <>
            <label style={styles.label}>Confirm password{req}</label>
            <input
              style={styles.input}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </>
        )}

        {error && <div style={styles.error}>{error}</div>}
        {notice && <div style={styles.notice}>{notice}</div>}

        <button className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} disabled={busy}>
          {busy ? m.busy : m.cta}
        </button>

        <div style={styles.links}>
          {mode === 'signin' && (
            <>
              <button type="button" style={styles.link} onClick={() => switchMode('forgot')}>Forgot password?</button>
              <span style={styles.linkRow}>New here? <button type="button" style={styles.link} onClick={() => switchMode('signup')}>Create an account</button></span>
            </>
          )}
          {mode === 'signup' && (
            <span style={styles.linkRow}>Already have an account? <button type="button" style={styles.link} onClick={() => switchMode('signin')}>Sign in</button></span>
          )}
          {mode === 'forgot' && (
            <button type="button" style={styles.link} onClick={() => switchMode('signin')}>← Back to sign in</button>
          )}
        </div>
      </form>
    </div>
  );
}

// Map common Firebase auth error codes to friendly copy.
function friendly(err) {
  const code = err?.code || '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Email or password is incorrect.';
    case 'auth/email-already-in-use':
      return 'An account already exists for that email. Try signing in.';
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/weak-password':
      return 'Please choose a stronger password (at least 8 characters).';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return err?.message || 'Something went wrong. Please try again.';
  }
}

const styles = {
  wrap: { minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: {
    width: 360, background: '#fff', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', padding: 28, boxShadow: 'var(--shadow-card)',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 },
  logo: { width: 32, height: 32, borderRadius: 8, objectFit: 'contain', display: 'block' },
  brandText: { fontWeight: 700, color: 'var(--ink-2)' },
  title: { fontSize: '1.25rem', marginBottom: 16, color: 'var(--ink)' },
  label: { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--ink-3)', margin: '10px 0 4px' },
  hint: { fontSize: '0.72rem', color: 'var(--muted)', marginTop: 4 },
  input: {
    width: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    padding: '9px 12px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
  },
  error: {
    marginTop: 12, background: 'var(--red-bg)', color: 'var(--red-text)',
    padding: '8px 10px', borderRadius: 'var(--radius-sm)', fontSize: '0.78125rem',
  },
  notice: {
    marginTop: 12, background: 'var(--green-bg, #ecfdf5)', color: 'var(--green-text, #15803d)',
    padding: '8px 10px', borderRadius: 'var(--radius-sm)', fontSize: '0.78125rem',
  },
  links: { marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' },
  linkRow: { fontSize: '0.8125rem', color: 'var(--muted)' },
  link: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    color: 'var(--indigo-600, #4f46e5)', fontSize: '0.8125rem', fontWeight: 600,
  },
};
